import {z} from 'zod';

const TestDriveStateSchema = z.enum(['planned', 'completed', 'cancelled']);

export const TestDriveDocumentResponseSchema = z.object({
  id: z.string(),
  filename: z.string(),
  pdfFilename: z.string(),
  fileUri: z.string(),
  pdfUri: z.string(),
  uploadedAt: z.string(),
});
export type TestDriveDocumentResponseBodyTestDriveNew = z.infer<
  typeof TestDriveDocumentResponseSchema
>;

export const TestDriveResponseSchema = z.object({
  id: z.string(),
  businessCaseId: z.string().nullable(),
  businessCaseCode: z.string().nullable(),
  vehicleId: z.string(),
  userId: z.string(),
  userFirstName: z.string(),
  userLastName: z.string(),
  customerId: z.string(),
  customerFoundingPersonFirstName: z.string().nullable(),
  customerFoundingPersonLastName: z.string().nullable(),
  customerTradeName: z.string().nullable(),
  state: TestDriveStateSchema,
  note: z.string().nullable(),
  internalNote: z.string().nullable(),
  customerFeedback: z.string().nullable(),
  customerName: z.string().nullable(),
  customerDriversLicence: z.string().nullable(),
  fuelBefore: z.number().nullable(),
  fuelAfter: z.number().nullable(),
  batteryChargeBefore: z.number().nullable(),
  batteryChargeAfter: z.number().nullable(),
  mileageBefore: z.number().nullable(),
  mileageAfter: z.number().nullable(),
  returnedAt: z.string().nullable(),
  scheduledAt: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
  documents: z.array(TestDriveDocumentResponseSchema),
});
export type TestDriveResponseBodyTestDriveNew = z.infer<typeof TestDriveResponseSchema>;

export const GetTestDriveArgSchema = z.object({
  id: z.string(),
});
export type GetTestDriveApiArgTestDriveNew = z.infer<typeof GetTestDriveArgSchema>;

export const ListTestDriveArgSchema = z.object({
  vehicleId: z.string(),
});
export type ListTestDriveApiArgTestDriveNew = z.infer<typeof ListTestDriveArgSchema>;

export const UpdateTestDriveRequestSchema = z.object({
  userId: z.string(),
  state: TestDriveStateSchema,
  note: z.string().nullable(),
  internalNote: z.string().nullable().optional(),
  customerFeedback: z.string().nullable().optional(),
  scheduledAt: z.string(),
  fuelBefore: z.number().nullable().optional(),
  fuelAfter: z.number().nullable().optional(),
  batteryChargeBefore: z.number().nullable().optional(),
  batteryChargeAfter: z.number().nullable().optional(),
  mileageBefore: z.number().nullable().optional(),
  mileageAfter: z.number().nullable().optional(),
  returnedAt: z.string().nullable().optional(),
  customerName: z.string().nullable().optional(),
  customerDriversLicence: z.string().nullable().optional(),
  vehicleId: z.string().optional(),
});
export type UpdateTestDriveRequestBody = z.infer<typeof UpdateTestDriveRequestSchema>;

export const UpdateTestDriveArgSchema = z.object({
  testDriveId: z.string(),
  businessCaseId: z.string().optional(),
  updateTestDriveRequestBody: UpdateTestDriveRequestSchema,
});
export type UpdateTestDriveApiArg = z.infer<typeof UpdateTestDriveArgSchema>;

export type DeleteTestDriveApiResponse = unknown;
export const DeleteTestDriveApiArgSchema = z.object({
  businessCaseId: z.string(),
  testDriveId: z.string(),
});
export type DeleteTestDriveApiArg = z.infer<typeof DeleteTestDriveApiArgSchema>;

export type GetTestDriveApiResponseTestDriveNew = TestDriveResponseBodyTestDriveNew;
export type ListTestDriveApiResponseTestDriveNew = TestDriveResponseBodyTestDriveNew[];
export type UpdateTestDriveApiResponse = TestDriveResponseBodyTestDriveNew;

export const RenderedTemplateResponseSchema = z.object({
  url: z.string(),
  pdfUrl: z.string(),
  fileId: z.string(),
  remoteFileId: z.string(),
});
export type RenderedTemplateResponseBodyTestDriveNew = z.infer<
  typeof RenderedTemplateResponseSchema
>;

export const CreateTestDriveDocumentRequestSchema = z.object({
  testDriveId: z.string(),
  templateId: z.string(),
});
export type CreateTestDriveDocumentRequestBody = z.infer<
  typeof CreateTestDriveDocumentRequestSchema
>;

export type CreateTestDriveDocumentApiResponse = RenderedTemplateResponseBodyTestDriveNew;

export const CreateTestDriveDocumentApiArgSchema = z.object({
  createTestDriveDocumentRequestBody: CreateTestDriveDocumentRequestSchema,
});
export type CreateTestDriveDocumentApiArg = z.infer<typeof CreateTestDriveDocumentApiArgSchema>;

export const VehicleConditionSchema = z.enum([
  'CONDITION_NEW',
  'CONDITION_USED',
  'CONDITION_DEMONSTRATION_CAR',
  'CONDITION_DAMAGED',
  'CONDITION_CLASSIC_CAR',
  'CONDITION_SPARE_PARTS',
]);
export type VehicleCondition = z.infer<typeof VehicleConditionSchema>;

export const SearchTestDriveCalendarArgSchema = z.object({
  from: z.string(),
  to: z.string(),
  search: z.string().nullable(),
  pagination: z.object({page: z.number(), size: z.number()}),
  includeVehiclesWithoutTestDrives: z.boolean().optional(),
});
export type SearchTestDriveCalendarApiArg = z.infer<typeof SearchTestDriveCalendarArgSchema>;

/** Only `planned` and `completed` reach the client — cancelled drives are excluded server-side. */
export const TestDriveCalendarDriveSchema = z.object({
  testDriveId: z.string(),
  state: z.enum(['planned', 'completed']),
  scheduledAt: z.string(),
  returnedAt: z.string().nullable(),
  hasReturnedAt: z.boolean(),
  customerId: z.string(),
  customerDisplayName: z.string().nullable(),
  customerPhoneNumbers: z.array(z.string()),
  businessCaseId: z.string().nullable(),
  note: z.string().nullable(),
  /**
   * The employee who took the car out
   */
  userId: z.string(),
  userDisplayName: z.string().nullable(),
});
export type TestDriveCalendarDrive = z.infer<typeof TestDriveCalendarDriveSchema>;

export const TestDriveCalendarVehicleSchema = z.object({
  vehicleId: z.string(),
  title: z.string(),
  registrationPlate: z.string().nullable(),
  vin: z.string().nullable(),
  branchId: z.string(),
  branchName: z.string(),
  testDrives: z.array(TestDriveCalendarDriveSchema),
});
export type TestDriveCalendarVehicle = z.infer<typeof TestDriveCalendarVehicleSchema>;

export const SearchTestDriveCalendarResponseSchema = z.object({
  totalCount: z.number(),
  vehicles: z.array(TestDriveCalendarVehicleSchema),
});
export type SearchTestDriveCalendarApiResponse = z.infer<
  typeof SearchTestDriveCalendarResponseSchema
>;

export const TestDriveCalendarSettingsSchema = z.object({
  branchIds: z.array(z.string()).min(1),
  vehicleConditions: z.array(VehicleConditionSchema).min(1),
  branchOverrides: z.array(
    z.object({
      branchId: z.string(),
      vehicleConditions: z.array(VehicleConditionSchema).min(1),
    })
  ),
});
export type TestDriveCalendarSettings = z.infer<typeof TestDriveCalendarSettingsSchema>;

export const UpdateTestDriveCalendarSettingsArgSchema = z.object({
  updateTestDriveCalendarSettingsRequestBody: TestDriveCalendarSettingsSchema,
});
export type UpdateTestDriveCalendarSettingsApiArg = z.infer<
  typeof UpdateTestDriveCalendarSettingsArgSchema
>;
export type UpdateTestDriveCalendarSettingsApiResponse = TestDriveCalendarSettings;
